import React, { FC } from "react";

import { useBoolean } from "~/hooks";

import { AttachFileType } from "~/domains";

import { Icon, DownloadLink, BackgroundThumbnailImage } from "~/ui";
import { FileContentTypeIcon, FileViewerModal } from "~/ui";

type PropsType = {
  className?: string;
  thumbnailImageUrl: string;
  attachFile?: AttachFileType;
  onPreviewsRequest?: () => void;
  title: string;
  tags?: string[];
  bookmark?: {
    request: () => void;
    bookmarked: boolean;
  };
  download?: {
    url: string;
    fileName: string;
    onSuccess?: () => void;
  };
  contentBgColorClassName?: "bg-primary-200" | "bg-transparent";
  displayMode: "navigate" | "preview";
};

export const Thumbnail: FC<PropsType> = ({
  attachFile,
  thumbnailImageUrl,
  title,
  className = "",
  tags = [],
  bookmark,
  download,
  contentBgColorClassName = "bg-primary-200",
  onPreviewsRequest,
  displayMode = "navigate",
}: PropsType) => {
  const bookmarkIcon = bookmark?.bookmarked
    ? "ioBookmark"
    : "ioBookmarkOutline";

  const {
    isChecked: isModalOpen,
    setTrue: setOpen,
    setFalse: setClose,
  } = useBoolean(false);

  const previewOpen = () => {
    setOpen();
    onPreviewsRequest && onPreviewsRequest();
  };

  return (
    <>
      <div
        className={`group relative overflow-hidden rounded-2xl bg-white shadow hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${className}`}
      >
        {/* サムネイル画像エリア */}
        <div className="relative aspect-video overflow-hidden rounded-t-2xl bg-gradient-to-br from-secondary-100 to-secondary-300">
          <BackgroundThumbnailImage
            bgImageUrl={thumbnailImageUrl}
            onClick={previewOpen}
          />

          {/* グラデーションオーバーレイ */}
          <div className="absolute inset-0 bg-gradient-to-t from-secondary-900/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* ブックマークボタン */}
          {bookmark && (
            <button
              type="button"
              onClick={bookmark.request}
              className="absolute z-layout top-3 left-3 p-2 rounded-full bg-white/90 backdrop-blur-xs shadow-lg hover:bg-white hover:scale-110 transition-all duration-200"
            >
              <Icon
                icon={bookmarkIcon}
                color="text-primary-600"
                size="1.25rem"
              />
            </button>
          )}

          {/* ダウンロードボタン */}
          {download && (
            <DownloadLink
              fileName={download.fileName}
              fileUrl={download.url}
              onSuccess={download.onSuccess}
              className="absolute z-layout top-3 right-3 p-2 rounded-full bg-white/90 backdrop-blur-xs shadow-lg hover:bg-white hover:scale-110 transition-all duration-200 opacity-0 group-hover:opacity-100"
            >
              <Icon
                icon="riDownloadCloudLine"
                color="text-primary-800"
                size="1.25rem"
              />
            </DownloadLink>
          )}
        </div>

        {/* コンテンツエリア */}
        <div className={`p-4 space-y-3 ${contentBgColorClassName}`}>
          <h3 className="font-semibold text-secondary-900 text-sm line-clamp-2 h-11 group-hover:text-primary-800 transition-colors duration-200">
            {title}
          </h3>

          {/* ファイルアイコンとタグ */}
          <div className="flex items-center gap-3">
            {/* ファイルアイコン */}
            {attachFile && (
              <div className="shrink-0 p-1.5 rounded-lg bg-secondary-200 hover:bg-secondary-300 transition-colors duration-200">
                <FileContentTypeIcon
                  contentType={attachFile.contentType}
                  iconSize="1rem"
                  className="shrink-0"
                />
              </div>
            )}

            {/* タグ */}
            <div className="flex-1 flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
              {tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 bg-primary-300 text-primary-800 hover:bg-primary-400"
                >
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary-300 text-secondary-600">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ホバー時のプレビューボタン */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-secondary-900/20 backdrop-blur-[1px]">
          <button
            type="button"
            onClick={previewOpen}
            className="px-4 py-2 bg-white rounded-full shadow-lg font-medium text-sm text-secondary-900 hover:bg-secondary-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300"
          >
            {displayMode === "navigate" ? "詳細" : "プレビュー"}
          </button>
        </div>
      </div>

      {attachFile && isModalOpen && (
        <FileViewerModal
          onClose={setClose}
          attachedFile={attachFile}
          title={title}
        />
      )}
    </>
  );
};
